import React, {useContext} from 'react'
import Tooltip from '@mui/material/Tooltip'
import includes from 'lodash/includes'
import map from 'lodash/map'
import split from 'lodash/split'
import classNames from 'classnames'
import moment from 'moment'
import { transliterate as tr } from 'transliteration/dist/browser/bundle.esm.min.js'
import { ProductContainer } from './types'
import { formatTenantDate } from '../../utils/formatTenantDateTime'
import { LocalizationContext } from '../shared/contexts'
import Icon from '../shared/icon'
import { t } from '../../i18n'
import { date } from 'yup'

const STATUS_OK = 'ok'
const STATUS_WARNING = 'warning'
const STATUS_ALERT = 'alert'

const scope = 'product_container'

const ProductContainerSlot: React.FC<{
  slotNumber: number,
  currentDate: moment.Moment,
  container: ProductContainer,
  expandContainer: Function,
  searchQuery: string,
  currentProductCategoryId: number,
  isLiquid?: boolean,
  hasDraftReplenishment?: boolean,
  replenishmentView?: boolean,
  hasSentReplenishment: boolean,
  productReplenishmentName?: string,
  isDisabled?: boolean,
  isFaulty?: boolean
}> = ({
  slotNumber, currentDate, container, expandContainer, searchQuery,
  currentProductCategoryId, isLiquid, hasDraftReplenishment, replenishmentView,
  hasSentReplenishment, productReplenishmentName, isDisabled, isFaulty
}) => {
  const localization = useContext(LocalizationContext)
  const { weight_scale } = localization

  const weightUnit = {
    'metric': 'kg',
    'imperial': 'lbs'
  }[weight_scale]

  const expandProductContainer = (slotNumber, isLiquid) => {
    expandContainer(slotNumber, isLiquid)
  }

  if(isDisabled) {
    return (
      <div
        className={`container-slot product empty disabled`}
      >
        <span className='container-edge top left'></span>
        <span className='container-edge top right'></span>
        <span className='container-edge bottom left'></span>
        <span className='container-edge bottom right'></span>
        <div className='container-info'>
          <div className='product-name-disabled'> Disabled </div>
          <div className='empty-container-slot-number'>
            #{slotNumber}
          </div>
        </div>
      </div>
    )
  }

  const hasActiveReplenishment = replenishmentView && (hasDraftReplenishment || hasSentReplenishment)

  if (!container) {
    return (
      <div
        className={`container-slot product empty ${(hasDraftReplenishment && !hasSentReplenishment) ? 'replenished' : ''} ${isFaulty ? 'faulty' : ''}`}
        onClick={() => expandProductContainer(slotNumber, isLiquid)}
      >
        <span className='container-edge top left'></span>
        <span className='container-edge top right'></span>
        {(replenishmentView && hasSentReplenishment) &&
          <span className='replenishment-sent-corner-status'></span>
        }
        <span className='container-edge bottom left'></span>
        <span className='container-edge bottom right'></span>
        <div className='container-info'>
          <div className='product-name'> {isFaulty ? 'faulty' : ((hasActiveReplenishment) ? productReplenishmentName : "")}</div>
          <div className='empty-container-slot-number'>
            #{slotNumber}
          </div>
        </div>
        <div className='add-button'>
          <i className='fa fa-plus-circle'/>
          <span style={{fontSize:"12px"}}>{t('replace', { scope })}</span>
        </div>
      </div>
    )
  }

  const {
    product, expiration_date, initial_amount, used_amount, show_alert, remaining_amount,
    remaining_amount_percent, remaining_amount_status, days_until_expiration, expiration_date_status
  } = container

  const formattedExpirationDate = formatTenantDate(expiration_date, localization)

  let expirationText: string
  if (days_until_expiration < 0) {
    expirationText = `expired`
  } else if (includes([STATUS_ALERT, STATUS_WARNING], expiration_date_status)) {
    expirationText = `${days_until_expiration} days left`
  } else {
    expirationText = formattedExpirationDate
  }

  const matchesFilters = () => {
    const trSearchQuery = tr(searchQuery.toLowerCase())
    const searchingByQuery = trSearchQuery.length > 0
    const trProductName = tr(product.name.toLowerCase())
    if (searchingByQuery && !includes(trProductName, trSearchQuery)) return false

    const searchingByCategory = !!currentProductCategoryId
    if(searchingByCategory) {
        const category = product.product_category
        const categoryAncestorIds = map(split(category.ancestry, '/'), idStr => parseInt(idStr))
        const categoryAncestryWithSelfIds = [...categoryAncestorIds, category.id]
        if (!includes(categoryAncestryWithSelfIds, currentProductCategoryId)) return false
    }

    return searchingByQuery || searchingByCategory
  }

  const containerClassNames = classNames({
    'highlighted': matchesFilters(),
    'replenished': hasDraftReplenishment,
    'warning': Object.keys(container.product_container_warnings).length > 0,
    'faulty': isFaulty
  })


  return (
    <div
      data-number={container.slot_number}
      className={`container-slot product ${containerClassNames}`}
      onClick={() => expandProductContainer(slotNumber, isLiquid)}
    >
      <span className='container-edge top left'></span>
      <span className='container-edge top right'></span>

      {show_alert && !(replenishmentView && (hasDraftReplenishment || hasSentReplenishment)) &&
        <span className='container-danger'></span>
      }
      {(replenishmentView && hasSentReplenishment) &&
        <span className='replenishment-sent-corner-status'></span>
      }

      <div className='container-info'>
        <div className='product-name'>{(hasActiveReplenishment) ? productReplenishmentName : product.name}</div>
        {!hasActiveReplenishment &&
          <Tooltip title={`${Math.round(remaining_amount_percent)}% / ${remaining_amount}${weightUnit} left`} placement='top' arrow>
            <div className={`amount-bar status-${remaining_amount_status}`}>
              <div className='current-amount-bar' style={{ width: `${remaining_amount_percent}%` }}></div>
            </div>
          </Tooltip>
        }
        <div className='d-flex justify-content-between align-items-center mt-2'>
          <div className='container-slot-number'>
              {`#${slotNumber}`}
          </div>
          {(hasDraftReplenishment && replenishmentView && !hasSentReplenishment) &&
          <div className="product-replenished-icon">
              <Icon name="replenishedProduct"/>
            </div>
          }
          {
            !hasActiveReplenishment && (
              isFaulty
                ? <div className='product-expiration-date status-faulty'>faulty</div>
                : <div className={`product-expiration-date status-${expiration_date_status}`}>
                    {expirationText}
                  </div>
            )
          }
        </div>
      </div>
    </div>
  )
}

export default ProductContainerSlot
